const getHash = () =>
    Date.now().toString(36) + Math.random().toString(36).substring(2)

const state = {
    items: [],
    secondary_items: [],
    arranged_items: [],
    itemsChanged: false,
    itemDeleted: false,
    orderChanged: false,
}

const getOrderPricing = (items, types) => {
    return items.reduce(
        (_pricing, item) => {
            if (!item.deleted) {
                const itemType = types.find(y => y.id == item.order_item_type_id);
                let itemCost =
                    (parseFloat(item.price) || 0) *
                    (parseFloat(item.quantity) || 0);

                // Apply item.discount only if itemType is not a discount type
                if (!itemType?.is_discount) {
                    itemCost -= parseFloat(item.discount) || 0;
                    _pricing.discount += parseFloat(item.discount) || 0; // Accumulate discount for non-discount items
                }

                _pricing.total += itemCost;
                _pricing.tax +=
                    (itemCost * (parseFloat(item.tax_rate) || 0)) / 100;
            }
            return _pricing;
        },
        { tax: 0, total: 0, discount: 0 }
    );
}

const getItems = (items, types, is_discount) => {
    return items.filter(x => {
        const itemType = types.find(y => y.id == x.order_item_type_id);
        if (is_discount) {
            return !x.deleted && !!itemType?.is_discount;
        } else {
            return !x.deleted && !itemType?.is_discount;
        }
        // Original commented code preserved
        // if (is_discount) {
        //     return !x.deleted && !!itemType?.is_discount
        // } else {
        //     return !x.deleted && !itemType?.is_discount
        // }
    });
}

const getAdjustmentTotals = (adjustmentItems, types) => {
    const defaultTitle = 'Discount';
    let title = defaultTitle;
    let description = defaultTitle;
    return adjustmentItems.reduce(
        (_pricing, item, idx) => {
            if (!item.deleted) {
                const itemType = types.find(y => y.id == item.order_item_type_id);
                let itemCost =
                    (parseFloat(item.price) || 0) *
                    (parseFloat(item.quantity) || 0);

                // Apply item.discount only if itemType is not a discount type
                if (!itemType?.is_discount) {
                    itemCost -= parseFloat(item.discount) || 0;
                    _pricing.discount += parseFloat(item.discount) || 0; // Accumulate discount for non-discount items
                }

                _pricing.total += itemCost;
                _pricing.tax +=
                    (itemCost * (parseFloat(item.tax_rate) || 0)) / 100;
                if (idx == 0) {
                    _pricing.title = defaultTitle || item.name;
                    _pricing.description = item.description;
                } else {
                    _pricing.title = defaultTitle;
                    _pricing.description = defaultTitle;
                }
            }
            return _pricing;
        },
        { tax: 0, total: 0, discount: 0, title, description }
    );
}

const getters = {
    orderPricing(state, getters, rootState) {
        return getOrderPricing(state.items, rootState.itemType.items);
    },

    itemsTotals(state, getters, rootState) {
        const types = rootState.itemType.items;
        const primary_totals = getOrderPricing(getters['itemsOnly'], types);
        const secondaryItemsOnly = getItems(state.secondary_items, types, false);
        const secondary_totals = getOrderPricing(secondaryItemsOnly, types);
        return {
            tax: primary_totals.tax + secondary_totals.tax,
            total: primary_totals.total + secondary_totals.total,
            discount: primary_totals.discount + secondary_totals.discount, // Combine discounts
        };
    },

    adjustmentTotals(state, getters, rootState) {
        const types = rootState.itemType.items;
        const { tax, total, discount, title, description } = getAdjustmentTotals(
            getters['adjustmentItems'],
            types
        );
        const secItemsOnly = getItems(state.secondary_items, types, true);
        const secAdjustTotals = getAdjustmentTotals(secItemsOnly, types);
        return {
            tax: tax + secAdjustTotals.tax,
            total: total + secAdjustTotals.total,
            discount: discount + secAdjustTotals.discount, // Combine discounts
            title,
            description,
        };
    },

    itemsTotalsCustomerPortal(state, getters, rootState) {
        const types = (rootState.itemType.items || []).filter(x => x.customer_access != 0);
        const primary_totals = getOrderPricing(getters['itemsOnly'], types);
        const secondaryItemsOnly = getItems(state.secondary_items, types, false);
        const secondary_totals = getOrderPricing(secondaryItemsOnly, types);
        return {
            tax: primary_totals.tax + secondary_totals.tax,
            total: primary_totals.total + secondary_totals.total,
            discount: primary_totals.discount + secondary_totals.discount, // Combine discounts
        };
    },

    adjustmentTotalsCustomerPortal(state, getters, rootState) {
        const types = (rootState.itemType.items || []).filter(x => x.customer_access != 0);
        const { tax, total, discount, title, description } = getAdjustmentTotals(
            getters['adjustmentItems'],
            types
        );
        const secItemsOnly = getItems(state.secondary_items, types, true);
        const secAdjustTotals = getAdjustmentTotals(secItemsOnly, types);
        return {
            tax: tax + secAdjustTotals.tax,
            total: total + secAdjustTotals.total,
            discount: discount + secAdjustTotals.discount, // Combine discounts
            title,
            description,
        };
    },

    itemsOnly(state, getters, rootState) {
        console.log('this is called----items---', state.items);
        const types = rootState.itemType.items;
        return getItems(state.items, types, false);
    },

    adjustmentItems(state, getters, rootState) {
        const types = rootState.itemType.items;
        return getItems(state.items, types, true);
    },

    hasPricing(state) {
        return !!state.items.length;
    },

    orderTotal(state, getters, rootState) {
        return getOrderPricing(state.items, rootState.itemType.items);
    },

    orderTax(state, getters, rootState) {
        return getOrderPricing(state.items, rootState.itemType.items);
    },
}

const mutations = {
    SET_ITEMS(state, items) {
        state.items = items;
    },
    SET_SECONDARY_ITEMS(state, items) {
        state.secondary_items = items;
    },
    SET_CHANGED(state, x) {
        state.itemsChanged = x;
    },
    SET_ITEM_DELETED(state, x) {
        state.itemDeleted = x;
    },
    SET_ORDER_CHANGED(state, x) {
        state.orderChanged = x;
    },
    ARRANGED_DATA(state, arranged_items) {
        state.arranged_items = arranged_items;
    },
}

const actions = {
    load({ commit }, initialItems) {
        console.log('initialItems', initialItems);
        const items = initialItems.map(x => {
            x.hash = x.hash || getHash();
            return x;
        });
        commit('SET_ITEMS', items);
        commit('SET_CHANGED', false);
        commit('SET_ITEM_DELETED', false);
    },

    clear({ commit }) {
        commit('SET_ITEMS', []);
        commit('SET_CHANGED', false);
        commit('SET_ITEM_DELETED', true);
    },
    clearAll({ commit }) {
        console.log('this clear all called--');
        commit('SET_ITEMS', []);
        commit('SET_SECONDARY_ITEMS', []);
        commit('ARRANGED_DATA', []);
        commit('SET_CHANGED', false);
        commit('SET_ITEM_DELETED', true);
    },

    merge({ state, commit }, item) {
        item.hash = item.hash || getHash();
        const items = [...state.items];
        // // Note: now we are allowing duplicate items in details, so need to comment below code. and added items.push(item) in the end of commented code
        // let index = items.findIndex(
        //     x => x.hash == item.hash || x.master_item_id == item.master_item_id
        // )
        // if (index >= 0) items.splice(index, 1, item)
        // else items.push(item)

        let index = items.findIndex(
            x => x.hash == item.hash
        );
        console.log('new item duplicate-item-event', item, index);

        if (index >= 0) items.splice(index, 1, item);
        else items.push(item);
        commit('SET_CHANGED', true);
        commit('SET_ITEMS', items);
    },

    delete({ state, commit }, hash) {
        const items = [...state.items];
        const index = items.findIndex(x => x.hash == hash);
        items.splice(index, 1);
        commit('SET_CHANGED', true);
        commit('SET_ITEMS', items);
        commit('SET_ITEM_DELETED', true);
    },
    deleteTemplate({ commit }) {
        commit('SET_ITEMS', []);
    },
    async arrange(context, payload) {
        console.log('payLoad', payload);
        context.commit('SET_ORDER_CHANGED', true);
        context.commit('ARRANGED_DATA', payload);
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}